import { toneColours, type Tone } from './statusTones';
import type { AppTokens } from './tokens';

/**
 * The `sx` that paints a chip in a tone. `StatusChip` uses it, and so does a calendar entry chip
 * (a clickable chip whose label is the entry, not the status). A clickable one keeps its fill on
 * hover and focus (MUI would re-tint it) and marks them with the default chip's cues: a 1px inset
 * `lineStrong` outline on hover, a `focusRing` outline on keyboard focus.
 */
export function toneChipSx(tokens: AppTokens, tone: Tone) {
  const { background, ink, border } = toneColours(tokens, tone);
  // The chip's icon (a pending flag) takes the ink too: `<tone>Text` on `<tone>Soft` is guaranteed, the
  // default chip's `text` icon is not on a tone wash.
  const fill = { backgroundColor: background, color: ink, '& .MuiChip-icon': { color: ink } };
  // No specificity override is needed: the fill and ink beat MUI's filled variant as `sx`. An earlier
  // axe contrast failure on the app bar was an artefact of axe running mid-transition after a
  // colour-scheme switch (TMMAPP-0092); the e2e settle helper waits for it.
  return {
    ...fill,
    '& .MuiChip-label': { color: ink },
    ...(border === null ? {} : { border: `1px solid ${border}` }),
    '&.MuiChip-clickable:hover': {
      ...fill,
      boxShadow: `inset 0 0 0 1px ${tokens.lineStrong}`,
    },
    '&.Mui-focusVisible': {
      ...fill,
      outline: `2px solid ${tokens.focusRing}`,
      outlineOffset: 2,
    },
  };
}
